import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Chip,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Phone as PhoneIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Phone, PhoneFormData } from '../types/Phone';
import PhoneService from '../services/PhoneService';
import PhoneDialog from './PhoneDialog';

const PhoneManagement: React.FC = () => {
  const [phones, setPhones] = useState<Phone[]>([]);
  const [filteredPhones, setFilteredPhones] = useState<Phone[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPhone, setEditingPhone] = useState<Phone | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [phoneToDelete, setPhoneToDelete] = useState<Phone | null>(null);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPhones();
  }, []);

  useEffect(() => {
    filterPhones();
  }, [phones, searchTerm]);

  const loadPhones = async () => {
    try {
      setLoading(true);
      const data = await PhoneService.getAllPhones();
      setPhones(data);
    } catch (error) {
      showSnackbar('Fehler beim Laden der Telefone', 'error');
      console.error('Fehler beim Laden der Telefone:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPhones = () => {
    if (!searchTerm) {
      setFilteredPhones(phones);
    } else {
      const filtered = phones.filter(phone => {
        const searchLower = searchTerm.toLowerCase();
        return (
          phone.macAddress.toLowerCase().includes(searchLower) ||
          phone.userId.toLowerCase().includes(searchLower) ||
          (phone.description && phone.description.toLowerCase().includes(searchLower))
        );
      });
      setFilteredPhones(filtered);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddPhone = () => {
    setEditingPhone(null);
    setDialogOpen(true);
  };

  const handleEditPhone = (phone: Phone) => {
    setEditingPhone(phone);
    setDialogOpen(true);
  };

  const handleDeleteClick = (phone: Phone) => {
    setPhoneToDelete(phone);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!phoneToDelete) return;

    try {
      await PhoneService.deletePhone(phoneToDelete._id);
      await loadPhones();
      showSnackbar('Telefon erfolgreich gelöscht', 'success');
    } catch (error) {
      showSnackbar('Fehler beim Löschen des Telefons', 'error');
      console.error('Fehler beim Löschen:', error);
    } finally {
      setDeleteDialogOpen(false);
      setPhoneToDelete(null);
    }
  };

  const handleSavePhone = async (phoneData: PhoneFormData) => {
    try {
      if (editingPhone) {
        await PhoneService.updatePhone(editingPhone._id, phoneData);
        showSnackbar('Telefon erfolgreich aktualisiert', 'success');
      } else {
        await PhoneService.createPhone(phoneData);
        showSnackbar('Telefon erfolgreich hinzugefügt', 'success');
      }
      
      setDialogOpen(false);
      await loadPhones();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      showSnackbar(`Fehler: ${errorMessage}`, 'error');
      console.error('Fehler beim Speichern:', error);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPhone(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <PhoneIcon />
        Telefon-Verwaltung
      </Typography>
      
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Verwalten Sie die Zuordnung von MAC-Adressen zu Benutzer-IDs für XML-Export über Telefone.
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Telefone durchsuchen..."
          placeholder="MAC-Adresse, Benutzer-ID oder Beschreibung"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
          }}
          sx={{ mb: 2 }}
        />

        <Typography variant="h6" gutterBottom>
          {filteredPhones.length} Telefone gefunden
        </Typography>

        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>MAC-Adresse</TableCell>
                  <TableCell>Benutzer-ID</TableCell>
                  <TableCell>Beschreibung</TableCell>
                  <TableCell>Erstellt</TableCell>
                  <TableCell>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPhones.map((phone) => (
                  <TableRow key={phone._id}>
                    <TableCell>
                      <Chip 
                        label={phone.macAddress} 
                        variant="outlined"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {phone.userId}
                      </Typography>
                    </TableCell>
                    <TableCell>{phone.description || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(phone.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEditPhone(phone)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(phone)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredPhones.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body1" color="textSecondary">
                        {searchTerm ? 'Keine Telefone gefunden.' : 'Noch keine Telefone vorhanden.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Fab
        color="primary"
        aria-label="add"
        onClick={handleAddPhone}
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
      >
        <AddIcon />
      </Fab>

      <PhoneDialog
        open={dialogOpen}
        phone={editingPhone}
        isEditing={!!editingPhone}
        onClose={handleCloseDialog}
        onSave={handleSavePhone}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Telefon löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie das Telefon mit MAC-Adresse{' '}
            <strong>{phoneToDelete?.macAddress}</strong> wirklich löschen?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PhoneManagement;
